import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { ExternalLink } from '@gjensidige/builders-icons';
import { useTracking, getText } from '../utils';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';
import type { TrackingProp } from '../types';
import clsx from 'clsx';
import styles from './link.module.css';

export type LinkProps<As extends React.ElementType = 'a'> =
  PolymorphicComponentPropWithRef<
    As,
    { icon?: 'auto' | boolean; tracking?: TrackingProp }
  >;

type LinkComponent = <As extends React.ElementType = 'a'>(
  props: LinkProps<As>
) => JSX.Element;

const EXTERNAL_TEXTS = {
  no: 'Åpner i nytt vindu',
  da: 'Åbner i nyt vindue',
  se: 'Öppnas i nytt fönster',
  en: 'Opens in new window'
};

export const Link: LinkComponent = forwardRef(function Link<
  As extends React.ElementType = 'a'
>(
  {
    as,
    children,
    className,
    icon = 'auto',
    onClick,
    tracking = true,
    ...rest
  }: LinkProps<As>,
  ref?: PolymorphicRef<As>
) {
  const Tag = as || 'a';
  const innerRef = useRef<As>(null);
  const isExternal = rest.target === '_blank';
  const track = useTracking(tracking);
  const handleClick = (event: React.MouseEvent) => {
    track({
      eventCategory: 'link',
      eventAction: 'click',
      eventLabel: getText(event.currentTarget)
    });
    onClick?.(event); // Track before onClick in case URL changes
  };

  useImperativeHandle(ref, () => innerRef.current as As);

  // Add aria-description to external links
  useEffect(() => {
    const self = innerRef.current as Element | null;
    const lang = isExternal && self?.closest('[lang]')?.getAttribute('lang');
    const text = EXTERNAL_TEXTS[lang as keyof typeof EXTERNAL_TEXTS];

    if (text) self?.setAttribute('aria-description', text);
    else if (isExternal) console.warn(`Invalid page language "${lang}"`);
    return () => self?.removeAttribute('aria-description');
  }, [isExternal]);

  if (rest.target === '_blank')
    Object.assign(rest, { rel: 'noreferrer noopener' });

  return (
    <Tag
      className={clsx(styles.link, className)}
      onClick={handleClick}
      ref={innerRef}
      {...rest}
    >
      {children}
      {isExternal && icon && <ExternalLink className={styles.external} />}
    </Tag>
  );
}) as LinkComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element
